import { MenuItemFormData } from "../DishUpdatePage/DishUpdatePage";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    CategoryInterface,
    DishForTree,
    PartialDishSupplement,
    PartialMenuItem,
} from "../../../interfaces/meMenuInterfaces/interfaces";
import { IPickedCategoryNames } from "../MenuItemsPage/constants";
import { normalizeCategoryData } from "../../../services/servicesMeMenuCategories/normalizeCategoryData";
import CategoryMeMenu, { MeMenuCategory } from "../../../services/servicesMeMenuCategories/CategoryMeMenu";
import ErrorService from "../../../services/ErrorService";
import {
    useCreateMenuCategoryForUpdateMenuItemMutation,
    useCreateMenuItemMutation,
    useGetDishSupplementByIdLazyQuery,
    useGetDishSupplementsNameAndIdQuery,
    useGetMenuAllergensForUpdateMenuItemQuery,
    useGetMenuCategoriesForUpdateMenuItemsQuery,
    useGetMenuMarkersForUpdateMenuItemQuery,
    useGetMenuTypesForUpdateMenuItemQuery,
    useGetUnitsForUpdateMenuItemQuery,
} from "@me-team/host/main/updateMenuItem/graphql/updateMenuItem.hooks";
import CategoryIconPickerWithLabel from "../components/CategoryIconPicker/CategoryIconPickerWithLabel";
import { useMenuCategoryIcons } from "../utils/hooks/useMenuCategoryIcons";
import { ErrorMessage, Field, FieldArray, Form, Formik, FormikHelpers } from "formik";
import {
    MultiSwitchField,
    PriceInput,
    ReactSelect,
    Spinner,
    SpinnerLoader,
    TableHeader,
    TextareaInputWithCounter,
} from "@services/ui-components";
import { menuItemValidationSchema } from "./constants";
import { Badge, Button, Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import CreateMenuItemFormEffects from "./CreateMenuItemFormEffects";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import { ServiceModalWrapper } from "@me-pos/service-modal";
import i18next from "i18next";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { getCookingTimeOptions } from "../DishUpdatePage/constants";
import { Option, OptionString } from "../../../interfaces/interfaces";
import TextInput from "@services/ui-components/src/TextInput/TextInput";
import PortionVolumeInput from "../DishUpdatePage/inputs/PortionVolumeInput";
import RequestInputService from "../../../services/servicesMeMenuCategories/RequestInputService";
import DishSupplementsInput from "./components/DishSupplementsInput";
import SupplementsGroupModal, {
    SupplementsGroupFormValues,
    SupplementsGroupSubmitValues,
} from "../SupplementsForDishesPage/modals/SupplementsGroupModal/SupplementsGroupModal";
import { mapDishesFromBackendToTreeDishes } from "../SupplementsForDishesPage/utils/dishesInputServices/mapDishesFromBackendToTreeDishes";
import {
    useCreateMenuSupplementMutation,
    useGetMenuItemsForSupplementsQuery,
    useUpdateMenuSupplementMutation,
} from "@me-team/host/main/dishSupplements/graphql/dishSupplements.hooks";
import { SupplementsGroupModalState } from "../SupplementsForDishesPage/SupplementsForDishesPage";
import { buildSupplementInput } from "../SupplementsForDishesPage/utils/buildSupplementInput";
import AddSupplementsToDishModal from "./modals/AddSupplementsToDishModal/AddSupplementsToDishModal";
import { getGraphqlErrorCategory } from "../SupplementsForDishesPage/utils/helpers";
import { useCreateDishParams } from "./hooks/useCreateDishParams";
import { getMenuTypeEnumOptionsBySlug } from "../AllMenusPage/menuEnumMappings";
import { CompanyAccess, MenuCategoryInput, MenuItemInput } from "@me-team/host/main/graphql/types";
import { useManageMenuItemCategoryRelationMutation } from "@me-team/host/main/editMenuPage/graphql/editMenuPage.hooks";
import MenuLanguageVersionSelectContainer from "../components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildPrimaryLanguageVm, buildSelectedLanguageVm } from "../SupplementsForDishesPage/utils/languageVm";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

const DishCreatePage = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const canCreateDish = useRoleAccess(CompanyAccess.MenuDishCreate);
    const canCreateDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryCreate);
    const canCreateSupplement = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsCreate);
    const canEditSupplement = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsEdit);
    const buttonCategoryRef = useRef<HTMLButtonElement>(null);

    const [showSupplementMenu, setShowSupplementMenu] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [isMeMenuCategoryModalOpen, setIsMeMenuCategoryModalOpen] = useState<boolean>(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryList, setCategoryList] = useState<CategoryInterface[]>(null);
    const [categoryInput, setCategoryInput] = useState(null);
    const [pickedCategoryIds, setPickedCategoryIds] = useState<number[]>([]);
    const cookingTimeOptions: OptionString[] = getCookingTimeOptions(t);
    const [modalState, setModalState] = useState<SupplementsGroupModalState>({
        isOpen: false,
        mode: "create",
        group: null,
    });
    const [listAddOnsRefetch, setListAddOnsRefetch] = useState<(() => Promise<unknown>) | null>(null);
    const [returnToAddSupplementsModal, setReturnToAddSupplementsModal] = useState(false);

    const [createMenuCategory] = useCreateMenuCategoryForUpdateMenuItemMutation();
    const [createMenuItem] = useCreateMenuItemMutation();
    const [updateMenuSupplement] = useUpdateMenuSupplementMutation();
    const [createMenuSupplement] = useCreateMenuSupplementMutation();

    const [getSupplementById] = useGetDishSupplementByIdLazyQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const {
        loading: menuLanguagesLoading,
        primaryLanguage,
        languages,
    } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageVm = useMemo(
        () => buildPrimaryLanguageVm(primaryLanguage, languages),
        [primaryLanguage, languages]
    );
    const selectedLanguageVm = useMemo(
        () => buildSelectedLanguageVm(selectedLangVersionCode, primaryLanguageVm, languages),
        [selectedLangVersionCode, primaryLanguageVm, languages]
    );

    const {
        data: meMenucategoriesData,
        refetch: refetchCategories,
        loading: meMenuCategoryLoading,
    } = useGetMenuCategoriesForUpdateMenuItemsQuery({
        variables: {
            companyId: Number(currentCompanyId),
            itemsPerPage: 1000,
            isDeleted: false,
            locale: primaryLanguageVm?.code,
        },
        skip: !currentCompanyId || !primaryLanguageVm?.code,
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems: MeMenuCategory[] = normalizeCategoryData(data);
            setCategoryList(CategoryMeMenu.categoryModuleCreator(normalizedItems, null, null));
            setCategoryInput(RequestInputService.createServiceCategoryInput(null, null, normalizedItems));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const categoryListData = meMenucategoriesData?.user?.company?.meMenuCategoriesPaginated?.categories;

    const categoryIcons = useMenuCategoryIcons(currentCompanyId);
    const handleSubmitCreateCategory = async (variables: MenuCategoryInput) => {
        if (!canCreateDishCategory) return;

        const input: MenuCategoryInput = {
            ...variables,
            parentCategoryId: typeof variables.parentCategoryId === "number" ? variables.parentCategoryId : null,
        };
        try {
            await createMenuCategory({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    input,
                    locale: primaryLanguageVm?.code,
                },
                context: {
                    errorType: "local",
                },
                onCompleted: (data) => {
                    data && setCurrentCategory(data.createMenuCategory);
                },
                onError: (error) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            });
        } catch (error) {
            console.error(error);
        }
    };

    const { data: unitsData, loading: unitsLoading } = useGetUnitsForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const { data: menuAllergensData, loading: allergensLoading } = useGetMenuAllergensForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: menuMarkersData, loading: markersLoading } = useGetMenuMarkersForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: menuTypesData, loading: menuTypesLoading } = useGetMenuTypesForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const [manageMenuItemCategoryRelation] = useManageMenuItemCategoryRelationMutation({
        context: {
            errorType: "local",
        },
    });

    const {
        data: dishSupplementsNameAndIdData,
        loading: dishSupplementsNameAndIdLoading,
        refetch: dishSupplementsNameAndIdRefetch,
    } = useGetDishSupplementsNameAndIdQuery({
        variables: {
            companyId: Number(currentCompanyId),
            isDeleted: false,
            onlyParent: true,
            locale: primaryLanguageVm?.code,
        },
        skip: !currentCompanyId || !primaryLanguageVm?.code,
        context: {
            errorType: "local",
        },
        fetchPolicy: "network-only",
        onError: (error) => {
            console.error(error);
        },
    });

    const supplementsNameAndId: PartialDishSupplement[] =
        dishSupplementsNameAndIdData?.user?.company?.menuSupplementsList;
    const supplementGroupsForInput = useMemo(
        () =>
            (supplementsNameAndId ?? []).map((supplement) => ({
                id: supplement.id,
                name: supplement.groupName,
                nameOrigin: supplement.groupNameOrigin,
            })),
        [supplementsNameAndId]
    );

    const unitOptions =
        unitsData?.relations?.units
            ?.filter((unit) => [9, 10, 6, 7].includes(unit.id))
            ?.map((unit) => ({
                label: unit.name,
                value: unit.id,
            })) || [];

    const handleRemoveCategory = (categoryId: number) => {
        setPickedCategoryIds((prev: number[]) => prev.filter((id) => id !== categoryId));
        setPickedCategoryNames((prev) => prev.filter((cat) => cat.id !== categoryId));
    };

    const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (buttonCategoryRef.current) {
            buttonCategoryRef.current.focus();
        }
    };

    const { data: dishesFromCategoriesData, loading: dishesFromCategoriesLoading } = useGetMenuItemsForSupplementsQuery(
        {
            variables: {
                companyId: Number(currentCompanyId),
                itemsPerPage: 100000,
                isDeleted: false,
                locale: primaryLanguageVm?.code,
            },
            skip: !currentCompanyId || !primaryLanguageVm?.code,
            context: {
                errorType: "local",
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        }
    );

    const dishesFromCategories: PartialMenuItem[] = dishesFromCategoriesData?.user?.company?.menuItems?.items;

    const dishesData: DishForTree[] = useMemo(
        () => mapDishesFromBackendToTreeDishes(dishesFromCategories),
        [dishesFromCategories]
    );

    const openCreateModal = (fromAddModal = false) => {
        if (!canCreateSupplement) return;

        if (fromAddModal) {
            setReturnToAddSupplementsModal(true);
        }
        setModalState({
            isOpen: true,
            mode: "create",
            group: null,
        });
    };

    const openEditModal = async (groupId: number) => {
        if (!canEditSupplement) return;
        if (!groupId || !currentCompanyId) return;
        const { data } = await getSupplementById({
            variables: { companyId: Number(currentCompanyId), supplementId: groupId, locale: primaryLanguageVm?.code },
        });
        const supplementGroup = data?.user?.company?.menuSupplementsList?.[0];
        if (supplementGroup) {
            setModalState({
                isOpen: true,
                mode: "edit",
                group: supplementGroup,
            });
        }
    };

    const handleCloseModal = () => {
        setModalState((prev) => ({
            ...prev,
            isOpen: false,
        }));

        if (returnToAddSupplementsModal) {
            setShowSupplementMenu(true);
            setReturnToAddSupplementsModal(false);
        }
    };

    const handleSubmitSupplementsGroupModal = async (
        values: SupplementsGroupSubmitValues,
        formikHelpers: FormikHelpers<SupplementsGroupFormValues>
    ) => {
        if (!currentCompanyId) return;

        const companyId = Number(currentCompanyId);
        const currentGroup = modalState.group;

        formikHelpers.setFieldError("groupName", undefined);

        try {
            if (modalState.mode === "create") {
                if (!canCreateSupplement) return;

                const input = buildSupplementInput(values);
                await createMenuSupplement({
                    variables: {
                        companyId,
                        branchId: Number(branchId),
                        input,
                        locale: primaryLanguageVm?.code,
                    },
                    context: { errorType: "local" },
                });
            }

            if (modalState.mode === "edit" && currentGroup) {
                if (!canEditSupplement) return;

                const input = buildSupplementInput(values);
                await updateMenuSupplement({
                    variables: {
                        companyId,
                        branchId: Number(branchId),
                        dishId: currentGroup.id,
                        input,
                        locale: selectedLangVersionCode,
                    },
                    context: { errorType: "local" },
                });
            }

            await dishSupplementsNameAndIdRefetch();

            if (typeof listAddOnsRefetch === "function") {
                await listAddOnsRefetch();
            }

            handleCloseModal();
        } catch (error) {
            const apolloError = error as ApolloError;
            const errorCategory = getGraphqlErrorCategory(error);
            if (errorCategory === "menu_supplement.already_exists") {
                formikHelpers.setFieldError("groupName", t("A group with this name and options already exists"));
                return;
            }
            setIsErrorModalOpen(ErrorService.errorHandling(apolloError));
        }
    };

    const handleSubmit = async (values: MenuItemFormData, { setErrors }: FormikHelpers<MenuItemFormData>) => {
        if (!canCreateDish) return;

        const input: MenuItemInput = {
            name: values.name,
            types: values.types,
            categories: values.categories.map((category) => category),
            unit: values.unit,
            portions:
                values.portions?.length > 0
                    ? values.portions.map((portion) => ({
                          name: portion.name,
                          volume: portion.volume,
                          price: portion.price,
                      }))
                    : [],
            description: values.description || null,
            composition: values.composition || null,
            cookingTime: values.cookingTime ? values.cookingTime : null,
            isLongCookingTime: values.isLongCookingTime,
            markers: values.markers ? values.markers?.map((marker) => marker) : null,
            allergens: values.allergens ? values.allergens?.map((allergen) => allergen) : null,
            supplements: values.supplements?.length > 0 ? values.supplements.map((supplement) => supplement) : null,
            isUpdateImage: values.isUpdateImage,
        };

        if (values?.isUpdateImage === true) input.image = values.image;

        if (currentCompanyId) {
            await createMenuItem({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    input,
                    locale: primaryLanguageVm?.code,
                },
                onCompleted: async (data) => {
                    if (hasCategories) {
                        await manageMenuItemCategoryRelation({
                            variables: {
                                companyId: Number(currentCompanyId),
                                branchId: Number(branchId),
                                menuId: menuId,
                                input: {
                                    menuItem: data.createMenuItem.id,
                                    category: childCategoryId ? childCategoryId : categoryId,
                                },
                            },
                        });
                        navigate(`/menu/page/${menuId}/branch/${branchId}`, {
                            state: { text: t("Created"), toast: true },
                        });
                    } else {
                        navigate(`/menu/dishes/branch/${branchId}`, { state: { text: t("Created"), toast: true } });
                    }
                },
                onError: (error: ApolloError) => {
                    const validationErrors = error?.graphQLErrors[0];
                    const validationErrorsCategory = validationErrors?.extensions?.category;
                    if (validationErrorsCategory === "menu_item.already_exists") {
                        setErrors({ name: t("A dish with this name already exists.") });
                    }
                    const validation = validationErrors?.extensions?.validation as {
                        image?: Array<{ message?: string }>;
                    };
                    const imageError = validation?.image?.[0]?.message?.replace(/\(\d+px\)/, "(width)");

                    if (imageError === "The image width is too small (width). Minimum width expected is 500px.") {
                        setErrors({ image: t("The image width is too small. Minimum width expected is 500px.") });
                    }
                },
            });
        }
    };

    const { pickedCategories, hasCategories, menuId, categoryId, childCategoryId, initFormValues } =
        useCreateDishParams({
            categoryListData,
            t,
        });

    const [pickedCategoryNames, setPickedCategoryNames] = useState<IPickedCategoryNames[]>(() => pickedCategories);

    const didInitCategoriesFromParamsRef = useRef(false);

    useEffect(() => {
        if (didInitCategoriesFromParamsRef.current) return;
        if (!pickedCategories || pickedCategories.length === 0) return;
        didInitCategoriesFromParamsRef.current = true;
        setPickedCategoryNames(pickedCategories);
        setPickedCategoryIds(pickedCategories.map((c) => c.id));
    }, [pickedCategories]);

    if (
        unitsLoading ||
        allergensLoading ||
        markersLoading ||
        menuTypesLoading ||
        dishSupplementsNameAndIdLoading ||
        dishesFromCategoriesLoading ||
        menuLanguagesLoading
    )
        return <Spinner />;

    return (
        <>
            <Formik<MenuItemFormData>
                initialValues={initFormValues}
                validationSchema={menuItemValidationSchema(t)}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ setFieldValue, isSubmitting, values, errors, touched }) => {
                    const handlePhoto = () => setFieldValue("isUpdateImage", true);
                    return (
                        <>
                            <TableHeader
                                title={t("New dish")}
                                paths={[
                                    { path: `/branches/${branchId}`, label: t("Branches") },
                                    { path: `/menu/dishes/branch/${branchId}`, label: t("List of dishes") },
                                    { path: "", label: t("New dish") },
                                ]}
                                buttons={
                                    <Row className="gx-3 justify-content-lg-end  mt-3 mt-lg-0">
                                        <Col
                                            sm={5}
                                            lg={6}
                                            xl={6}
                                            className="mb-2 mb-xxl-0"
                                            style={{ minWidth: "230px" }}
                                        >
                                            <div className="position-relative">
                                                <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                                    {t("Language version")}
                                                </span>
                                                <MenuLanguageVersionSelectContainer
                                                    companyId={Number(currentCompanyId)}
                                                    isLockedToPrimary
                                                />
                                                <div
                                                    className="position-absolute top-50 end-0 translate-middle-y "
                                                    style={{ height: "16px" }}
                                                >
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip>
                                                                {t("Translations can be added when editing")}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <div
                                                            className=" position-absolute top-50 translate-middle-y end-0 pt-1"
                                                            style={{ marginRight: "36px" }}
                                                        >
                                                            <i className="bi bi-info-circle"></i>
                                                        </div>
                                                    </OverlayTrigger>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                }
                                colSize={6}
                                btnColSize={6}
                            />
                            <Form>
                                <CreateMenuItemFormEffects
                                    pickedCategoryIds={pickedCategoryIds}
                                    menuTypes={menuTypesData?.menuTypes}
                                />
                                <Row className="mt-6">
                                    <Col lg={9} className="pe-lg-5">
                                        <h5 className="fs-24 text-capitalize mb-3 mb-lg-4">{t("Basic information")}</h5>
                                        <div>
                                            <div className="mb-3">
                                                <Row className="align-items-center">
                                                    <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="addDish-name-field"
                                                            className="fs-7 fw-bold required-label"
                                                        >
                                                            {t("Dish name")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <Field
                                                            id="addDish-name-field"
                                                            name="name"
                                                            placeholder={t("Add dish name")}
                                                            maxLength={100}
                                                            className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col sm={9} md={10} className="ms-auto">
                                                        <ErrorMessage name="name">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                        <div className="fs-7 text-grey mt-1">
                                                            {values.name?.length}/100
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <Row className="mb-3 py-lg-2">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label className="fs-7 fw-bold required-label d-inline">
                                                        {t("Available for")}
                                                    </label>
                                                    <OverlayTrigger
                                                        overlay={
                                                            <Tooltip>
                                                                <span className="d-block w-100 text-start">
                                                                    {t(
                                                                        "Select which menu types this dish should be available for"
                                                                    )}
                                                                </span>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i className="bi bi-info-circle ms-1 fs-7"></i>
                                                    </OverlayTrigger>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="d-flex justify-content-start flex-wrap gap-3">
                                                        <MultiSwitchField
                                                            name="types"
                                                            options={getMenuTypeEnumOptionsBySlug(
                                                                menuTypesData?.menuTypes,
                                                                (name) => t(name)
                                                            )}
                                                        />
                                                    </div>
                                                    <div className="ms-auto">
                                                        <ErrorMessage name="types">
                                                            {(msg) => (
                                                                <div className="invalid-feedback d-block">{msg}</div>
                                                            )}
                                                        </ErrorMessage>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3 py-lg-2">
                                                <Col sm={3} md={2} className="mb-2 mb-md-0">
                                                    <label
                                                        htmlFor="addDish-category-field"
                                                        className="text-dark fs-7 fw-bold py-1 required-label"
                                                        onClick={handleLabelClick}
                                                    >
                                                        {t("Category")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <div className="d-flex">
                                                        {pickedCategoryNames?.length > 0 ? (
                                                            <div className="d-flex flex-wrap gap-3 me-4">
                                                                {pickedCategoryNames.map(
                                                                    (category: IPickedCategoryNames) => (
                                                                        <Badge
                                                                            bg="additional-grey"
                                                                            className="rounded-1 px-2 d-flex align-items-center"
                                                                        >
                                                                            <span className="fs-16 me-2 mb-lg-0 text-dark">
                                                                                {category.name}
                                                                                {category.parentName ? (
                                                                                    <>
                                                                                        <i className="bi bi-arrow-right mx-1 "></i>{" "}
                                                                                        {category.parentName}
                                                                                    </>
                                                                                ) : null}
                                                                            </span>
                                                                            <Button
                                                                                className="bg-transparent border-0 text-dark p-0"
                                                                                onClick={() =>
                                                                                    handleRemoveCategory(category.id)
                                                                                }
                                                                            >
                                                                                <i className="bi bi-x-lg"></i>
                                                                            </Button>
                                                                        </Badge>
                                                                    )
                                                                )}
                                                            </div>
                                                        ) : null}
                                                        <div>
                                                            <Button
                                                                ref={buttonCategoryRef}
                                                                variant={`${touched.categories && errors.categories ? "outline-danger" : "outline-primary"}`}
                                                                className="fw-normal py-1"
                                                                id="addDish-categories-field"
                                                                style={{ width: "149px", maxHeight: "31px" }}
                                                                onClick={() => {
                                                                    setIsMeMenuCategoryModalOpen(true);
                                                                }}
                                                            >
                                                                {t("Select Category")}
                                                            </Button>
                                                            <ErrorMessage name="categories">
                                                                {(msg) => (
                                                                    <div className="text-danger d-block fs-7 mt-1">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="addDish-description-field"
                                                        className="fs-7 fw-bold pt-sm-6"
                                                    >
                                                        {t("Description dish")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <TextareaInputWithCounter
                                                        id="addDish-description-field"
                                                        name="description"
                                                        maxLength={400}
                                                        inputCol={12}
                                                        inputColSm={12}
                                                        placeholder={t("Add description")}
                                                        disabledLabel
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="addDish-composition-field"
                                                        className="fs-7 fw-bold text-break pt-sm-6"
                                                    >
                                                        {t("Short composition")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <TextareaInputWithCounter
                                                        id="addDish-composition-field"
                                                        name="composition"
                                                        maxLength={400}
                                                        inputCol={12}
                                                        inputColSm={12}
                                                        placeholder={t("Add short composition")}
                                                        disabledLabel
                                                    />
                                                </Col>
                                            </Row>
                                            <Row className="mb-3">
                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                    <label
                                                        htmlFor="addDish-unit-field"
                                                        className="fs-7 fw-bold text-break required-label"
                                                    >
                                                        {t("Units of measurement")}
                                                    </label>
                                                </Col>
                                                <Col sm={9} md={10}>
                                                    <ReactSelect
                                                        id="addDish-unit-field"
                                                        name="unit"
                                                        options={unitOptions}
                                                        value={unitOptions.find(
                                                            (option: Option) => option.value === values.unit
                                                        )}
                                                        onChange={(selectedOption: Option) => {
                                                            setFieldValue("unit", selectedOption.value);
                                                        }}
                                                        placeholder={t("Select Unit of Measurement")}
                                                        isSearchable={false}
                                                    />
                                                    <ErrorMessage name="unit">
                                                        {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                    </ErrorMessage>
                                                </Col>
                                            </Row>
                                        </div>
                                        <FieldArray
                                            name="portions"
                                            render={({ remove, push }) => (
                                                <div className="my-4 my-lg-5">
                                                    <h5 className="fs-24 mb-3 mb-lg-4">{t("Servings")}</h5>
                                                    {values?.portions?.map((_, index) => {
                                                        const nameId = `addDish-portions.${index}.name-field`;
                                                        const volumeId = `addDish-portions.${index}.volume-field`;
                                                        const priceId = `addDish-portions.${index}.price-field`;
                                                        return (
                                                            <Row key={index} className="mb-3">
                                                                <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                                    <label
                                                                        htmlFor={nameId}
                                                                        className="fs-7 fw-bold pt-sm-6 required-label"
                                                                    >
                                                                        {t("Serving name")}
                                                                    </label>
                                                                </Col>
                                                                <Col sm={9} md={10}>
                                                                    <Row>
                                                                        <Col xs={12} xxl={3} className="mb-3 mb-xxl-0">
                                                                            <TextInput
                                                                                name={`portions.${index}.name`}
                                                                                id={nameId}
                                                                                inputCol={12}
                                                                                inputColSm={12}
                                                                                placeholder="Serving name"
                                                                                isLabel={false}
                                                                                label=""
                                                                                maxLength={50}
                                                                            />
                                                                        </Col>
                                                                        <Col
                                                                            xxl={4}
                                                                            lg={5}
                                                                            sm={12}
                                                                            className="mb-3 mb-lg-0"
                                                                        >
                                                                            <Row className="gx-2">
                                                                                <Col xs={5}>
                                                                                    <label
                                                                                        htmlFor={volumeId}
                                                                                        className="fs-7 fw-bold required-label"
                                                                                    >
                                                                                        {t("Weight/ Volume dish")}
                                                                                    </label>
                                                                                </Col>
                                                                                <Col xs={7}>
                                                                                    <PortionVolumeInput
                                                                                        name={`portions.${index}.volume`}
                                                                                        id={volumeId}
                                                                                        placeholder={"0.0"}
                                                                                        initialValue={
                                                                                            values.portions[index]
                                                                                                .volume
                                                                                        }
                                                                                    />
                                                                                </Col>
                                                                            </Row>
                                                                        </Col>
                                                                        <Col md={12} lg={7} xxl={5}>
                                                                            <Row className="gx-2 align-items-center">
                                                                                <Col xs={5} lg={3}>
                                                                                    <label
                                                                                        htmlFor={priceId}
                                                                                        className="fs-7 fw-bold pt-6 required-label"
                                                                                    >
                                                                                        {t("Price")}
                                                                                    </label>
                                                                                </Col>
                                                                                <Col xs={7} lg={9} className="d-flex">
                                                                                    <PriceInput
                                                                                        name={`portions.${index}.price`}
                                                                                        placeholder="0.00"
                                                                                        id={priceId}
                                                                                        initialValue={
                                                                                            values.portions[index].price
                                                                                        }
                                                                                        classWrapperField="w-100"
                                                                                    />
                                                                                    {values.portions.length > 1 &&
                                                                                        index >= 1 && (
                                                                                            <div className="ms-2">
                                                                                                <Button
                                                                                                    type="button"
                                                                                                    variant="outline-secondary"
                                                                                                    className="w-100 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                                                                    onClick={() =>
                                                                                                        remove(index)
                                                                                                    }
                                                                                                >
                                                                                                    <i className="bi bi-trash3 text-dark"></i>
                                                                                                </Button>
                                                                                            </div>
                                                                                        )}
                                                                                </Col>
                                                                            </Row>
                                                                        </Col>
                                                                        {index === values.portions.length - 1 && (
                                                                            <Col xs={12} className="mt-3">
                                                                                <Button
                                                                                    type="button"
                                                                                    variant="light"
                                                                                    className="fs-7 text-primary p-0 border-0 bg-transparent"
                                                                                    onClick={() =>
                                                                                        push({
                                                                                            name: "",
                                                                                            volume: "",
                                                                                            price: "",
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    <i className="bi bi-plus me-1"></i>
                                                                                    {t("Add another portion option")}
                                                                                </Button>
                                                                            </Col>
                                                                        )}
                                                                    </Row>
                                                                </Col>
                                                            </Row>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        />

                                        <h5 className="fs-24 mb-3 mb-lg-4">{t("Additional Information")}</h5>
                                        <Row className="mb-3">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="addDish-cookingTime-field"
                                                    className="fs-7 fw-bold text-break pt-sm-6"
                                                >
                                                    {t("Cooking time")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <ReactSelect
                                                    id="addDish-cookingTime-field"
                                                    name="cookingTime"
                                                    options={cookingTimeOptions}
                                                    value={cookingTimeOptions.find(
                                                        (option: OptionString) => option.value === values.cookingTime
                                                    )}
                                                    onChange={(selectedOption: OptionString) => {
                                                        setFieldValue("cookingTime", selectedOption.value);
                                                    }}
                                                    placeholder={t("Select cooking time")}
                                                    isSearchable={false}
                                                />
                                                <ErrorMessage
                                                    name="cookingTime"
                                                    component="div"
                                                    className="invalid-feedback"
                                                />
                                                <div className="form-check mt-2 mb-0">
                                                    <Field
                                                        type="checkbox"
                                                        name="isLongCookingTime"
                                                        id="addDish-isLongCookingTime-field"
                                                        className="form-check-input"
                                                        checked={values.isLongCookingTime}
                                                    />
                                                    <label
                                                        className="form-check-label fs-7"
                                                        htmlFor="addDish-isLongCookingTime-field"
                                                    >
                                                        {t('Display icon "long cooking"')}
                                                    </label>
                                                    <OverlayTrigger
                                                        overlay={
                                                            <Tooltip>
                                                                {t(
                                                                    "There will be a marker next to this dish in the menu"
                                                                )}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i className="bi bi-info-circle ms-1 fs-7 pt-1"></i>
                                                    </OverlayTrigger>
                                                    <ErrorMessage
                                                        name="isLongCookingTime"
                                                        component="div"
                                                        className="text-danger"
                                                    />
                                                </div>
                                            </Col>
                                        </Row>
                                        <Row className="mb-3">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label htmlFor="addDish-warning-field" className="fs-7 fw-bold pt-sm-6">
                                                    {t("Warning")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <div className="d-flex justify-content-start align-content-center flex-wrap border rounded-1 p-6 gap-3">
                                                    <MultiSwitchField
                                                        name="markers"
                                                        options={(menuMarkersData?.menuMarkers ?? []).map((marker) => ({
                                                            value: marker.id,
                                                            label: marker.name,
                                                        }))}
                                                    />
                                                </div>
                                                <small className="text-grey">
                                                    {t("Optional small icons placed next to the dish name")}
                                                </small>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label
                                                    htmlFor="addDish-allergen-field"
                                                    className="fs-7 fw-bold pt-sm-6"
                                                >
                                                    {t("Allergen list")}
                                                </label>
                                            </Col>
                                            <Col sm={9} md={10}>
                                                <div className="d-flex justify-content-start flex-wrap border rounded-1 p-6 gap-3">
                                                    <MultiSwitchField
                                                        name="allergens"
                                                        options={(menuAllergensData?.menuAllergens ?? []).map(
                                                            (allergen, index) => ({
                                                                value: allergen.id,
                                                                label: (
                                                                    <>
                                                                        <span className="fs-7 text-primary-grey me-1">
                                                                            {index + 1}
                                                                        </span>
                                                                        <span className="text-grey">
                                                                            ({allergen.name})
                                                                        </span>
                                                                    </>
                                                                ),
                                                            })
                                                        )}
                                                    />
                                                </div>
                                                <small className="text-grey">
                                                    {t(
                                                        "Small numbers placed next to the dish name. Food ingredients that must be declared as allergens in the EU"
                                                    )}
                                                </small>
                                            </Col>
                                        </Row>

                                        <Row className="mb-4 mb-lg-5">
                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                <label htmlFor="addDish-supplements-field" className="fs-7 fw-bold">
                                                    {t("List of options/additives")}
                                                </label>
                                            </Col>

                                            <Col sm={9} md={10}>
                                                <div className="d-flex align-items-center w-100 mb-3">
                                                    <div className="w-100">
                                                        <DishSupplementsInput
                                                            id="addDish-supplements-field"
                                                            selectedIds={values.supplements || []}
                                                            groups={supplementGroupsForInput}
                                                            onRemove={(groupId) => {
                                                                const updated = (values.supplements || []).filter(
                                                                    (id) => id !== groupId
                                                                );
                                                                setFieldValue("supplements", updated);
                                                            }}
                                                            onEdit={(groupId) => {
                                                                if (canEditSupplement) openEditModal(groupId);
                                                            }}
                                                            canEditSupplement={canEditSupplement}
                                                        />
                                                    </div>
                                                    <Button
                                                        onClick={() => setShowSupplementMenu(true)}
                                                        type="button"
                                                        variant="outline-secondary"
                                                        className="h-100 ms-3 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                    >
                                                        <i className="bi bi-plus-lg text-dark"></i>
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col lg={3} className="ms-auto ps-lg-0">
                                        <h5 className="fs-24 text-capitalize mb-4">{t("Photo dish")}</h5>
                                        <ImageCrop
                                            setFieldValue={setFieldValue}
                                            fieldName="image"
                                            modalTitle={t("Dish Photo")}
                                            notBGinfo
                                            isMenuInfo
                                            fieldSetter={handlePhoto}
                                        />
                                        <ErrorMessage name="image">
                                            {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                        </ErrorMessage>
                                    </Col>
                                </Row>
                                <div className="d-sm-flex my-4 mt-lg-0">
                                    <Button
                                        id={"addDish-cancel-button"}
                                        onClick={() => {
                                            if (hasCategories) {
                                                navigate(`/menu/page/${menuId}/branch/${branchId}`);
                                            } else {
                                                navigate(`/menu/dishes/branch/${branchId}`);
                                            }
                                        }}
                                        variant="outline-primary"
                                        className="fw-normal mb-3 mb-sm-0 me-3 custom-btn rounded-1"
                                    >
                                        {t("Cancel")}
                                    </Button>
                                    <Button
                                        id={"addDish-submit-button"}
                                        type="submit"
                                        variant="primary"
                                        disabled={isSubmitting}
                                        className="fw-bold custom-btn rounded-1"
                                    >
                                        {isSubmitting ? (
                                            <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                        ) : (
                                            t("Save")
                                        )}
                                    </Button>
                                </div>
                            </Form>
                            <AddSupplementsToDishModal
                                show={showSupplementMenu}
                                onClose={() => {
                                    setShowSupplementMenu(false);
                                }}
                                onCreateNew={() => {
                                    if (!canCreateSupplement) return;
                                    setShowSupplementMenu(false);
                                    openCreateModal(true);
                                }}
                                initialSelectedIds={values.supplements || []}
                                onApply={(selectedIds) => {
                                    setFieldValue("supplements", selectedIds);
                                }}
                                onRefetchReady={setListAddOnsRefetch}
                                versionLocale={selectedLangVersionCode}
                                canCreateNewSupplement={canCreateSupplement}
                            />

                            {modalState.isOpen && dishesData && (
                                <SupplementsGroupModal
                                    show={modalState.isOpen}
                                    handleClose={handleCloseModal}
                                    onSave={handleSubmitSupplementsGroupModal}
                                    dishesData={dishesData}
                                    existingGroup={modalState.group || undefined}
                                    mode={modalState.mode}
                                    primaryLanguage={primaryLanguageVm}
                                    selectedLanguage={selectedLanguageVm}
                                />
                            )}
                        </>
                    );
                }}
            </Formik>

            {isMeMenuCategoryModalOpen && meMenucategoriesData && !meMenuCategoryLoading ? (
                <ServiceModalWrapper
                    companyId={Number(currentCompanyId)}
                    branchId={Number(branchId)}
                    i18n={i18next}
                    selectedCategoryId={null}
                    selectedSubcategoryId={null}
                    apolloUrl={process.env.REACT_APP_API_URL}
                    isOpen={isMeMenuCategoryModalOpen}
                    onClose={() => {
                        setIsMeMenuCategoryModalOpen(!isMeMenuCategoryModalOpen);
                    }}
                    onCategorySelect={(selectedCategory) => {
                        if (selectedCategory) {
                            setPickedCategoryIds((prev) => {
                                if (!prev.includes(selectedCategory.id)) {
                                    return [...prev, selectedCategory.id];
                                }
                                return prev;
                            });

                            setPickedCategoryNames((prev: any) => {
                                if (!prev.some((cat: any) => cat.id === selectedCategory.id)) {
                                    return [
                                        ...prev,
                                        {
                                            id: selectedCategory.id,
                                            name: selectedCategory.name,
                                            parentName: null,
                                        },
                                    ];
                                }
                                return prev;
                            });
                        }
                        setIsMeMenuCategoryModalOpen(!isMeMenuCategoryModalOpen);
                    }}
                    onSubcategorySelect={(selectedSubcategory) => {
                        if (selectedSubcategory) {
                            setPickedCategoryIds((prev) => {
                                if (!prev.includes(selectedSubcategory.id)) {
                                    return [...prev, selectedSubcategory.id];
                                }
                                return prev;
                            });

                            setPickedCategoryNames((prev: any) => {
                                if (!prev.some((cat: any) => cat.id === selectedSubcategory.id)) {
                                    return [
                                        ...prev,
                                        {
                                            id: selectedSubcategory.id,
                                            name: selectedSubcategory.name,
                                            parentName: selectedSubcategory?.parentCategory?.name || null,
                                        },
                                    ];
                                }
                                return prev;
                            });
                        }
                        setIsMeMenuCategoryModalOpen(!isMeMenuCategoryModalOpen);
                    }}
                    categoryList={categoryList}
                    categoryInput={categoryInput}
                    categoryListData={categoryListData}
                    refetchCategories={refetchCategories}
                    onSubmitCreate={handleSubmitCreateCategory}
                    canCreateCategory={canCreateDishCategory}
                    currentCategory={currentCategory}
                    categoryService={CategoryMeMenu}
                    requestInputService={RequestInputService}
                    normalizeCategoryData={normalizeCategoryData}
                    renderCategoryIconPicker={({ value, onChange, disabled }) => (
                        <CategoryIconPickerWithLabel
                            icons={categoryIcons}
                            value={value}
                            onChange={onChange}
                            disabled={disabled}
                            basePath={process.env.REACT_APP_POS_URL}
                        />
                    )}
                />
            ) : null}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default DishCreatePage;
